import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '../services/authentification.service';
import { AppConfigService } from '../services/app-config.service';
import { API_PATHS } from '../constants/api-paths';
import {
  HTTP_HEADER_NAMES,
  HTTP_HEADER_VALUES,
} from '../constants/http-headers';

/**
 * Attache le Bearer EasyLoc, {@code withCredentials} vers le Launcher,
 * et retente une fois après refresh en cas de 401.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private static readonly RETRY_HEADER = HTTP_HEADER_NAMES.retryAfterRefresh;
  private static readonly PUBLIC_AUTH_SUFFIXES = [
    API_PATHS.authLogin,
    API_PATHS.authRefresh,
    API_PATHS.authLogout,
  ];

  constructor(
    private readonly authService: AuthenticationService,
    private readonly appConfig: AppConfigService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const requestWithCredentials = this.withCredentialsIfLauncherApi(req);

    if (this.isPublicAuthRequest(requestWithCredentials)) {
      return next.handle(requestWithCredentials);
    }

    const authedRequest = this.addAuthToken(requestWithCredentials);
    return next.handle(authedRequest).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, requestWithCredentials, next))
    );
  }

  private withCredentialsIfLauncherApi(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const launcherApiUrl = this.appConfig.getLauncherApiUrl();
    if (!launcherApiUrl || !req.url.startsWith(launcherApiUrl)) {
      return req;
    }

    return req.clone({ withCredentials: true });
  }

  private isPublicAuthRequest(req: HttpRequest<unknown>): boolean {
    return AuthInterceptor.PUBLIC_AUTH_SUFFIXES.some(suffix => req.url.includes(suffix));
  }

  private isEasylocApiRequest(req: HttpRequest<unknown>): boolean {
    const apiUrl = this.appConfig.getApiUrl();
    return !!apiUrl && req.url.startsWith(apiUrl);
  }

	private addAuthToken(req: HttpRequest<unknown>): HttpRequest<unknown> {
    if (this.authService.isStubAuth() || !this.isEasylocApiRequest(req)) {
      return req;
    }

    const token = this.authService.getAuthToken();
    if (!token || req.headers.has(HTTP_HEADER_NAMES.authorization)) {
      return req;
    }

    let headers = req.headers.set(
      HTTP_HEADER_NAMES.authorization,
      `Bearer ${token}`
    );
    const isFormData = req.body instanceof FormData;
    if (!isFormData && !headers.has(HTTP_HEADER_NAMES.contentType)) {
      headers = headers.set(
        HTTP_HEADER_NAMES.contentType,
        HTTP_HEADER_VALUES.applicationJson
      );
    }

    return req.clone({ headers });
  }

  private handleError(
    error: HttpErrorResponse,
    originalRequest: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (error.status !== 401 || originalRequest.headers.has(AuthInterceptor.RETRY_HEADER)) {
      return throwError(() => error);
    }

    if (!this.isEasylocApiRequest(originalRequest)) {
      return throwError(() => error);
    }

    return this.authService.refreshAccessToken().pipe(
      switchMap(refreshed => {
        if (!refreshed) {
          if (this.authService.isStubAuth()) {
            return throwError(() => error);
          }
          this.authService.abandonSession();
          this.authService.redirectToLauncherLogin(window.location.pathname);
          return throwError(() => error);
        }

        const retryRequest = this.addAuthToken(originalRequest).clone({
          headers: originalRequest.headers.set(AuthInterceptor.RETRY_HEADER, 'true')
        });
        return next.handle(retryRequest);
      })
    );
  }
}
